// React Hooks:
import React, { useState } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  createFolderAction,
  createFolderCloseAction,
  createFolder,
} from "./createFolderSlice";
import { directory, searchInputAction } from "../../directory/directorySlice";
// MUI Components:
import {
  Modal,
  Popover,
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
// MUI Styles:
import { makeStyles } from "@mui/styles";

// Type Controller
interface CreateFolderProps {
  id: "ignoreDisableTouch" | undefined;
  openPopover: boolean;
  popoverCreateFolder: HTMLButtonElement | null;
  handleCloseCreateFolder: () => void;
}

const useStyles = makeStyles((theme) => ({
  CreateFolderBox: {
    width: "400px",
    padding: "24px",
    borderRadius: "8px",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("md")]: {
      width: "300px",
    },
  },
  field: {
    borderRadius: "5px",
    backgroundColor: "#F2F2F2",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F2F2F2",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      "& fieldset": {
        borderColor: "#F2F2F2",
        borderRadius: "5px",
      },
      "&:hover fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
    },
  },
  backdropStyle: {
    "& .css-i9fmh8-MuiBackdrop-root-MuiModal-backdrop": {
      backdropFilter: "blur(2px)",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  modalContainer: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    borderRadius: "8px",
    "&:focus": {
      outline: "none",
    },
  },
  images: {
    color: theme.palette.secondary.light,
    objectFit: "cover",
  },
}));
const CreateFolder: React.FC<CreateFolderProps> = ({
  id,
  openPopover,
  popoverCreateFolder,
  handleCloseCreateFolder,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { openModal } = useAppSelector(createFolder);
  const {
    directoryData: { items, path, idfolder },
  } = useAppSelector(directory);

  const [folderName, setFolderName] = useState("");

  const submitHandler = (event: any) => {
    event.preventDefault();

    if (!!items.find((item) => item.name === folderName.trim()) === false) {
      dispatch(searchInputAction(""));
      dispatch(
        createFolderAction({
          path: path,
          name: folderName.trim(),
          idfolder: idfolder,
        })
      );
      setFolderName("");
      dispatch(createFolderCloseAction());
      handleCloseCreateFolder();
    } else {
      enqueueSnackbar("You have already created a folder with the same name!", {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Popover
        id={id}
        open={openPopover}
        anchorEl={popoverCreateFolder}
        onClose={() => {
          handleCloseCreateFolder();
          dispatch(createFolderCloseAction());
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          m: "10px 0px 0px 35px",
          [theme.breakpoints.down("md")]: {
            margin: "10px 0px 0px -15px",
          },
          [theme.breakpoints.down("sm")]: {
            display: "none",
          },
        }}
      >
        <Box
          component="form"
          onSubmit={submitHandler}
          className={classes.CreateFolderBox}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              margin: "0px 0px 20px 0px",
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
              }}
            >
              Folder Name
            </Typography>

            <TextField
              inputRef={(input) => input && input.focus()}
              name="folderName"
              placeholder="Enter Name"
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "12px 8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                },
                width: "100%",
              }}
              className={classes.field}
            />
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              color="tertiary"
              fullWidth
              type="reset"
              sx={{
                width: "100px",
                height: "40px",
                fontSize: "18px",
                borderRadius: "8px",
                textTransform: "none",
                lineHeight: "0px",
                padding: "12px",
                mr: "8px",
                color: "inherit",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
              onClick={() => {
                setFolderName("");
                dispatch(createFolderCloseAction());
                handleCloseCreateFolder();
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="success"
              fullWidth
              type="submit"
              disabled={!!folderName ? false : true}
              sx={{
                width: "100px",
                height: "40px",
                fontSize: "18px",
                borderRadius: "8px",
                textTransform: "none",
                lineHeight: "0px",
                padding: "12px",
                transition: "all 0.4s",
                ":disabled": {
                  color: theme.palette.primary.contrastText,
                  boxShadow: "none",
                  backgroundColor: theme.palette.success.main,
                  opacity: "0.3",
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>

      <Modal
        open={openModal}
        onClose={() => {
          dispatch(createFolderCloseAction());
          handleCloseCreateFolder();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        container={() => document.getElementById("modalContainer")}
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
          "&:focus": {
            outline: "none",
          },
          [theme.breakpoints.up("sm")]: {
            display: "none",
          },
        }}
        className={classes.backdropStyle}
      >
        <Box
          component="form"
          onSubmit={submitHandler}
          sx={{
            width: "85%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 12,
            p: { xs: "24px" },
            transition: "all 0.5s",
            borderRadius: "8px",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.secondary,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              margin: "0px 0px 20px 0px",
            }}
          >
            <Typography
              sx={{
                width: "100%",
                padding: "8px 8px",
                lineHeight: "1",
                fontSize: "1rem",
              }}
            >
              Folder Name
            </Typography>

            <TextField
              inputRef={(input) => input && input.focus()}
              name="folderName"
              placeholder="Enter Name"
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              size={"small"}
              sx={{
                label: { color: (theme) => theme.palette.text.secondary },
                input: {
                  padding: "12px 8px",
                  color: (theme) => theme.palette.text.secondary,
                  "&::placeholder": {
                    fontSize: "14px",
                    color: (theme) => theme.palette.text.secondary,
                  },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                    WebkitTextFillColor: (theme) =>
                      theme.palette.text.secondary,
                    transition:
                      " background-color 600000s 0s, color 600000s 0s",
                  },
                },
                width: "100%",
              }}
              className={classes.field}
            />
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              color="tertiary"
              fullWidth
              type="reset"
              sx={{
                width: "100px",
                height: "40px",
                fontSize: "18px",
                borderRadius: "8px",
                textTransform: "none",
                lineHeight: "0px",
                padding: "12px",
                mr: "8px",
                color: "inherit",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
              onClick={() => {
                setFolderName("");
                dispatch(createFolderCloseAction());
                handleCloseCreateFolder();
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="success"
              fullWidth
              type="submit"
              disabled={!!folderName ? false : true}
              sx={{
                width: "100px",
                height: "40px",
                fontSize: "18px",
                borderRadius: "8px",
                textTransform: "none",
                lineHeight: "0px",
                padding: "12px",
                transition: "all 0.4s",
                ":disabled": {
                  color: theme.palette.primary.contrastText,
                  boxShadow: "none",
                  backgroundColor: theme.palette.success.main,
                  opacity: "0.3",
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default CreateFolder;
